import { ActionButton } from '@aibees/elephant/components/action-button';
import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { useIsItemSaving } from '@aibees/elephant/hooks/use-is-item-saving';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { SearchType } from '@aibees/elephant/types';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { LoadingButton } from '@mui/lab';
import { memo, MouseEventHandler, useCallback } from 'react';
import * as Styled from './styles';
import type { CompanySaveProps as Props } from './types';

export const CompanySave = memo(
  ({ className, item, isLookalike: _isLookalike, fullWidth }: Props) => {
    const { user } = useWorkspace() ?? {};
    const notify = useNotify();
    const { saving } = useIsItemSaving(item, SearchType.Company);
    const { mutateAsync } = useSaveRecord(SearchType.Company);
    const { addProcessingItems } = useActionButtonContext();

    const isSaved = !!item?.saved;
    const itemId = item?.id;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleClick = useCallback<MouseEventHandler>(
      async (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (!itemId) return;

        addProcessingItems({ id: itemId, type: 'SAVING' });

        await mutateAsync({
          kind: 'ADD',
          ids: [itemId],
          overwriteOwner: false,
          user,
          labels: [],
        });

        notify.success('Account Successfully Saved.');
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [itemId, user, addProcessingItems]
    );

    const handleWrapperClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <Styled.Wrapper
        onClick={handleWrapperClick}
        className={classnames('CompanySave', className, { fullWidth })}
      >
        {!isSaved ? (
          <LoadingButton
            className={classnames('CompanySave', className)}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClick}
            fullWidth={fullWidth}
          >
            {saving ? 'Saving...' : 'Save'}
          </LoadingButton>
        ) : (
          <ActionButton
            item={item}
            searchType={SearchType.Company}
            fullWidth={fullWidth}
          />
        )}
      </Styled.Wrapper>
    );
  }
);

CompanySave.displayName = 'CompanySave';
